import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { getElementData } from '../api/reports.api';
import {
  REPORT_ELEMENT_DATA_PAGINATED_QUERY_KEY,
  REPORT_ELEMENT_DATA_QUERY_KEY,
} from '../constants/reports-query-keys';
import { DateFilter, ReportData } from '../types';

export const useElementData = (
  reportId: string,
  elementId: string,
  filters: DateFilter,
  enabled: boolean
) => {
  return useQuery({
    queryKey: [...REPORT_ELEMENT_DATA_QUERY_KEY, reportId, elementId, filters],
    queryFn: () => getElementData(reportId, elementId, filters),
    staleTime: 2 * 60 * 1000,
    enabled: enabled && !!reportId && !!elementId,
  });
};

const TABLE_PAGE_SIZE = 50;

export const useElementDataPaginated = (
  reportId: string,
  elementId: string,
  filters: DateFilter,
  enabled: boolean
) => {
  const query = useInfiniteQuery({
    queryKey: [...REPORT_ELEMENT_DATA_PAGINATED_QUERY_KEY, reportId, elementId, filters],
    queryFn: ({ pageParam }) =>
      getElementData(reportId, elementId, filters, { cursor: pageParam, limit: TABLE_PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.totalCount == null) return undefined;
      const loaded = allPages.reduce((sum, p) => sum + p.rows.length, 0);
      return loaded < lastPage.totalCount ? loaded : undefined;
    },
    staleTime: 2 * 60 * 1000,
    enabled: enabled && !!reportId && !!elementId,
  });

  const firstPage = query.data?.pages[0];
  const mergedData: ReportData | undefined = firstPage
    ? {
        ...firstPage,
        rows: query.data?.pages.flatMap(p => p.rows) ?? [],
      }
    : undefined;

  return { ...query, data: mergedData };
};
